import React, { useEffect, useState, useRef } from "react";
import { Link } from 'react-router-dom';
import ApiClient from "../services/api-client";
import { useTranslation } from 'react-i18next';
import useStore from "../hooks/useStore";
import MyDataHelpModal from "../components/modals/mydata-help.component";
import Commons from '../util/commons';
import {
  ResponsiveContainer, BarChart, Bar,
  LineChart, Line, XAxis, YAxis,
  CartesianGrid, Tooltip, Legend
} from "recharts";


function useHiddenTotals() {
  const [hidden, setHidden] = useState(false);

  useEffect(() => {
    const _ht = Commons.getDefaults().hideTotals || false;
    setHidden(_ht);
  }, []);

  const toggle = () => {
    setHidden(prev => {
      const newVal = !prev;
      Commons.setDefault("hideTotals", newVal);
      return newVal;
    });
  };

  return [hidden, toggle];
}

export default function Dashboard() {
  const { t } = useTranslation();
  const comp = useStore.getState().selectedCompany;
  const [content, setContent] = useState({});
  const [notifications, setNotifications] = useState([]);
  const [myDataWarning, setMyDataWarning] = useState(false);
  const [hiddenTotals, toggleHiddenTotals] = useHiddenTotals();
  const refMyDataHelp = useRef();

  const chartRef1 = useRef();
  const chartRef2 = useRef();

  useEffect(() => {
    if (!comp) return;

    ApiClient.getCompanyDashboard(comp.id).then(resp => {
      const d = resp?.data || {};
      setContent({
        averageLasMonthPerDay: d.averageLasMonthPerDay,
        companyName: comp.name,
        subjectField: comp.subjectField,
        taxOffice: comp.taxOffice?.name || "",
        taxpayerId: comp.taxpayerId,
        countSaleInstances: d.countSaleInstances,
        countProducts: d.countProducts,
        countCustomers: d.countCustomers,
        totalWithVat: parseFloat(d.totalWithVat || 0).toFixed(2),
        totalsCurrentYear: parseFloat(d.totalsCurrentYear || 0).toFixed(2),
        chartData: d.chartData,
        chartMonthData: d.chartMonthData,
        userRemainingDays: d.userRemainingDays
      });
    }).catch(console.error);

    if (!comp.myDataUserId || !comp.myDataToken) {
      setMyDataWarning(true);
    }

    ApiClient.getNotifications().then(resp => {
      setNotifications(resp?.data || []);
    }).catch(console.error);
  }, [comp, t]);

  const STATUS_ICON = {
    success: 'fa-check-circle',
    warning: 'fa-exclamation-triangle',
    danger:  'fa-times-circle',
    info:    'fa-info-circle',
    primary: 'fa-bell',
  };

  const companyLink = comp ? `/usercompany/${comp.id}` : "#";

  // Toggle charts visibility on toggle
  useEffect(() => {
    const display = hiddenTotals ? "none" : "flex";
    [chartRef1, chartRef2].forEach(r => {
      if (r.current) r.current.style.display = display;
    });
  }, [hiddenTotals]);

  return (
    <div className="container-fluid salesContainer">
      {/* Header Cards */}
      <header className="row">
        {/* Company Info */}
        <Card title={content.companyName} subtitle={content.subjectField} value={
           <p>
            {t("taxpayerId")}: {content.taxpayerId} – {t("taxOffice")}: {content.taxOffice}
            {content.userRemainingDays > 0 && content.userRemainingDays <= 3 && (
              <span style={{ color: "white", marginLeft: '1rem' }}>
                {t("userRemainingDays", { userRemainingDays: content.userRemainingDays })}
              </span>
            )}
          </p>
        }>
         
          <span className="cardLink"><Link to={companyLink}>{t("EditCompany")}</Link></span>

        </Card>

        <Card title={t("noOfSales")} value={content.countSaleInstances}>
          <span className="cardLink"><Link to="/sale">{t("InvoicePublication")}</Link></span>
        </Card>

        <Card title={t("noOfCustomers")} value={content.countCustomers}>
          <span className="cardLink"><Link to="/customers">{t("mycustomers")}</Link></span>
        </Card>

        <Card title={t("totalsLastMonth")} value={hiddenTotals ? "***" : content.totalWithVat} refValue={content.averageLasMonthPerDay} large>
          <span className="ms-2">{t("avePricePerDay")}: <span id="avePricePerDay"><b>{content.averageLasMonthPerDay}</b> </span></span>
          <button className="btn btn-skin hideTotals" onClick={toggleHiddenTotals}>
            <i className={`fa ${hiddenTotals ? "fa-eye-slash" : "fa-eye"}`}></i>
          </button>
        </Card>
      </header>

      {/* Charts & Notifications */}
      <section ref={chartRef1} className="row chart-card-container">
        <div className="col-md-8 card chart-card">
          <ResponsiveContainer height={400}>
            <BarChart margin={{ top: 16, left: -20, right: 16, bottom: 0 }} data={content.chartMonthData}>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(1,50,137,0.08)" />
              <XAxis dataKey="month_year" tick={{ fill: '#666', fontSize: 12 }} axisLine={{ stroke: 'rgba(1,50,137,0.15)' }} tickLine={false} />
              <YAxis type="number" dataKey={(v) => parseFloat(v.totals)} allowDataOverflow={true} domain={[0, dataMax => ((dataMax + dataMax / 10).toFixed(0))]} tick={{ fill: '#666', fontSize: 12 }} axisLine={false} tickLine={false} />
              <Tooltip contentStyle={{ borderRadius: '8px', border: '1px solid rgba(1,50,137,0.12)', boxShadow: '0 4px 12px rgba(1,50,137,0.1)', fontSize: '13px' }} />
              <Legend wrapperStyle={{ fontSize: '12px', color: '#555', paddingTop: '8px' }} />
              <Bar dataKey="totals" barSize={22} fill="#013289" name={t("totalsPerMonthCY")} radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
        <div className="col-md-4 card chart-card notif-panel">
          <div className="card-header card-header-skin notif-panel-header">
            <i className="fa fa-bell-o" style={{ marginRight: 8 }}></i>
            {t("LatestNotifications")}
            {(notifications.length + (myDataWarning ? 1 : 0)) > 0 && (
              <span className="notif-badge">{notifications.length + (myDataWarning ? 1 : 0)}</span>
            )}
          </div>
          <div className="card-body notif-panel-body">

            {myDataWarning && (
              <div className="notif-item notif-item--warning">
                <i className={`fa ${STATUS_ICON.warning} notif-icon`}></i>
                <span className="notif-text">{t("mydataInfo2_1")}{t("mydataInfo2_3")}</span>
                <Link to={`/usercompany/${comp.id}`} className="notif-btn notif-btn--warning">
                  {t("mydataInfo2_2")}
                </Link>
              </div>
            )}

            {notifications.length === 0 && !myDataWarning && (
              <div className="notif-empty">
                <i className="fa fa-check-circle notif-empty-icon"></i>
                <span>{t("noNotifications") || "Δεν υπάρχουν ειδοποιήσεις"}</span>
              </div>
            )}

            {notifications.map((el, i) => {
              const status = el.status?.toLowerCase() || 'info';
              return (
                <div key={i} className={`notif-item notif-item--${status}`}>
                  <i className={`fa ${STATUS_ICON[status] || STATUS_ICON.info} notif-icon`}></i>
                  <span className="notif-text">{el.message}</span>
                  {el.link && (
                    <a className={`notif-btn notif-btn--${status}`} target="_blank" rel="noopener noreferrer" href={el.link}>
                      {t("clickHere")} <i className="fa fa-external-link"></i>
                    </a>
                  )}
                </div>
              );
            })}

          </div>
        </div>
      </section>

      <section ref={chartRef2} className="row chart-card-container">
        <div className="col-md-12 card chart-card">
          <ResponsiveContainer height={400}>
            <LineChart data={content.chartData} margin={{ top: 16, left: -20, right: 16, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(1,50,137,0.08)" />
              <XAxis dataKey="invoiceDate" tick={{ fill: '#666', fontSize: 12 }} axisLine={{ stroke: 'rgba(1,50,137,0.15)' }} tickLine={false} />
              <YAxis dataKey={d => parseFloat(d.totalsPerDay)} allowDataOverflow={true} domain={[0, max => (max * 1.1).toFixed(0)]} tick={{ fill: '#666', fontSize: 12 }} axisLine={false} tickLine={false} />
              <Tooltip contentStyle={{ borderRadius: '8px', border: '1px solid rgba(1,50,137,0.12)', boxShadow: '0 4px 12px rgba(1,50,137,0.1)', fontSize: '13px' }} />
              <Legend wrapperStyle={{ fontSize: '12px', color: '#555', paddingTop: '8px' }} />
              <Line type="monotone" dataKey="totalsPerDay" stroke="#013289" strokeWidth={2.5} dot={{ fill: '#013289', r: 3, strokeWidth: 0 }} activeDot={{ r: 5, fill: '#013289' }} name={t("totalsPerDay")} />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </section>

      <MyDataHelpModal ref={refMyDataHelp} />
    </div>
  );
}

// Reusable Card component
function Card({ title, subtitle, value, children, large }) {
  return (
    <div className="col-md-3">
      <div className={`card dashboardCard h-100 ${large ? 'dashboardCard--large' : ''}`}>
        <div className="card-header">
          <div className="d-flex">
            <div className="flex-1">
              <h5>{title}</h5>
              {subtitle && <h6>{subtitle}</h6>}
              {value !== undefined && <h5>{value}</h5>}
            </div>
          </div>
        </div>
        <div className="card-body">
          {children}
        </div>
      </div>
    </div>
  );
}
